import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { safeFormatRelative } from '../../../shared';
import {
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  IconButton,
  Box,
  Divider,
  Tooltip,
  Chip,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  Add as AddIcon,
  Psychology as PsychologyIcon,
  BugReport as InvestigationIcon,
  MoreVert as MoreVertIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import { InvestigationSummary, InvestigationScope } from '../../../types';
import InvestigationScopeModal from './InvestigationScopeModal';
import InvestigationDetailsDialog from './InvestigationDetailsDialog';
import InvestigationContextMenu from './InvestigationContextMenu';
import DeleteConfirmDialog from '../../../shared/components/DeleteConfirmDialog';

interface InvestigationSidebarProps {
  investigations: InvestigationSummary[];
  currentInvestigationId?: string;
  onNewInvestigation: () => void;
  onSelectInvestigation: (investigationId: string) => void;
  onDeleteInvestigation: (investigationId: string) => void;
  onManageScope: (investigationId: string, newScope: InvestigationScope[]) => void;
  onUpdateStatus: (investigationId: string, state: 'active' | 'completed' | 'archived') => void;
  onUpdateMetadata: (investigationId: string, updates: { title?: string; description?: string }) => void;
}

// Cap how many rows each status group renders before a "Show more" — customers
// can accumulate 100+ investigations, and rendering them all janks the sidebar.
const COLLAPSED_GROUP_SIZE = 15;

const InvestigationSidebar: React.FC<InvestigationSidebarProps> = ({
  investigations,
  currentInvestigationId,
  onNewInvestigation,
  onSelectInvestigation,
  onDeleteInvestigation,
  onManageScope,
  onUpdateStatus,
  onUpdateMetadata,
}) => {
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedInvestigation, setSelectedInvestigation] = useState<string | null>(null);
  const [showScopeModal, setShowScopeModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [showCompleted, setShowCompleted] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});

  // Keep the open investigation visible even when its state group is toggled off
  // (e.g. deep-linking a completed/archived session while the default view is
  // active-only) so the sidebar can highlight it.
  useEffect(() => {
    if (!currentInvestigationId) return;
    const current = investigations.find(inv => inv.id === currentInvestigationId);
    if (current?.state === 'completed') setShowCompleted(true);
    if (current?.state === 'archived') setShowArchived(true);
  }, [currentInvestigationId, investigations]);

  const selectedInvestigationObj = useMemo(
    () => investigations.find(inv => inv.id === selectedInvestigation),
    [investigations, selectedInvestigation]
  );

  const handleMenuOpen = useCallback((event: React.MouseEvent<HTMLElement>, investigationId: string) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
    setSelectedInvestigation(investigationId);
  }, []);

  const handleMenuClose = useCallback(() => {
    setMenuAnchor(null);
  }, []);

  const handleDeleteClick = useCallback(() => {
    setShowDeleteConfirm(true);
    handleMenuClose();
  }, [handleMenuClose]);

  const handleDeleteConfirm = useCallback(() => {
    if (selectedInvestigation) {
      onDeleteInvestigation(selectedInvestigation);
    }
    setShowDeleteConfirm(false);
    setSelectedInvestigation(null);
  }, [selectedInvestigation, onDeleteInvestigation]);

  const handleDeleteCancel = useCallback(() => {
    setShowDeleteConfirm(false);
  }, []);

  const handleManageScope = useCallback(() => {
    setShowScopeModal(true);
    handleMenuClose();
  }, [handleMenuClose]);

  const handleScopeModalSave = useCallback((newScope: InvestigationScope[]) => {
    if (selectedInvestigation) {
      onManageScope(selectedInvestigation, newScope);
    }
    setShowScopeModal(false);
    setSelectedInvestigation(null);
  }, [selectedInvestigation, onManageScope]);

  const handleScopeModalCancel = useCallback(() => {
    setShowScopeModal(false);
    setSelectedInvestigation(null);
  }, []);

  const handleViewDetails = useCallback((investigationId?: string) => {
    if (investigationId) {
      setSelectedInvestigation(investigationId);
    }
    setShowDetailsModal(true);
    handleMenuClose();
  }, [handleMenuClose]);

  const handleDetailsModalClose = useCallback(() => {
    setShowDetailsModal(false);
    setSelectedInvestigation(null);
  }, []);

  const handleDetailsModalSave = useCallback((investigationId: string, updates: { title?: string; description?: string }) => {
    onUpdateMetadata(investigationId, updates);
  }, [onUpdateMetadata]);

  const handleUpdateStatus = useCallback((state: 'active' | 'completed' | 'archived') => {
    if (selectedInvestigation) {
      onUpdateStatus(selectedInvestigation, state);
    }
  }, [selectedInvestigation, onUpdateStatus]);

  const filteredInvestigations = useMemo(() =>
    investigations.filter(inv => {
      const state = inv.state || 'active';
      if (state === 'completed') return showCompleted;
      if (state === 'archived') return showArchived;
      return true;
    }),
    [investigations, showArchived, showCompleted]
  );

  const groupedInvestigations = useMemo(() => {
    const groups = filteredInvestigations.reduce((acc, investigation) => {
      const state = investigation.state || 'active';
      if (!acc[state]) {
        acc[state] = [];
      }
      acc[state].push(investigation);
      return acc;
    }, {} as Record<string, InvestigationSummary[]>);
    Object.values(groups).forEach(list =>
      list.sort((a, b) => (b.updated_at || '').localeCompare(a.updated_at || ''))
    );
    return groups;
  }, [filteredInvestigations]);

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.paper',
        borderRight: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Box sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <PsychologyIcon color="primary" />
          <Typography variant="h6" color="primary">
            Investigations
          </Typography>
        </Box>

        <Box sx={{ width: '100%', mb: 2 }}>
          <Tooltip title="Start new investigation">
            <Box
              onClick={onNewInvestigation}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                borderRadius: 2,
                py: 1,
                cursor: 'pointer',
                '&:hover': {
                  bgcolor: 'primary.dark',
                },
              }}
            >
              <AddIcon sx={{ mr: 1 }} />
              <Typography variant="button">New Investigation</Typography>
            </Box>
          </Tooltip>
        </Box>

        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={showCompleted}
              onChange={(e) => setShowCompleted(e.target.checked)}
            />
          }
          label={
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Show Completed
            </Typography>
          }
          sx={{ ml: 0.5 }}
        />

        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
            />
          }
          label={
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Show Archived
            </Typography>
          }
          sx={{ ml: 0.5 }}
        />
      </Box>

      <Divider />

      <List sx={{ flex: 1, overflow: 'auto', px: 1 }}>
        {Object.keys(groupedInvestigations).length === 0 ? (
          <ListItem>
            <ListItemText
              secondary="No investigations yet"
              sx={{ textAlign: 'center', opacity: 0.6 }}
            />
          </ListItem>
        ) : (
          Object.entries(groupedInvestigations).map(([status, investigationsInGroup]) => {
            const expanded = expandedGroups[status];
            let visible = expanded
              ? investigationsInGroup
              : investigationsInGroup.slice(0, COLLAPSED_GROUP_SIZE);
            // Always keep the open investigation visible, even beyond the cap.
            if (!expanded && currentInvestigationId && !visible.some(i => i.id === currentInvestigationId)) {
              const selected = investigationsInGroup.find(i => i.id === currentInvestigationId);
              if (selected) visible = [selected, ...visible];
            }
            const hiddenCount = investigationsInGroup.length - visible.length;
            return (
            <Box key={status}>
              <Typography
                variant="overline"
                sx={{
                  px: 2,
                  py: 1,
                  display: 'block',
                  color: 'text.secondary',
                  fontWeight: 'bold',
                  textTransform: 'capitalize',
                }}
              >
                {status} ({investigationsInGroup.length})
              </Typography>

              {visible.map((investigation) => (
                <ListItem key={investigation.id} disablePadding sx={{ mb: 1 }}>
                  <ListItemButton
                    selected={investigation.id === currentInvestigationId}
                    onClick={() => onSelectInvestigation(investigation.id)}
                    sx={{
                      borderRadius: 2,
                      mx: 1,
                      opacity: investigation.state === 'completed' ? 0.6 : 1,
                      '&.Mui-selected': {
                        bgcolor: 'primary.light',
                        color: 'primary.contrastText',
                        '&:hover': {
                          bgcolor: 'primary.main',
                        },
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <InvestigationIcon
                        color={investigation.id === currentInvestigationId ? 'inherit' : 'primary'}
                      />
                    </ListItemIcon>

                    {/* The state chip sits on the date row below the title: beside it,
                        a drawer's width left the title a few characters wide. */}
                    <ListItemText
                      primary={
                        <Typography
                          variant="subtitle2"
                          sx={{ overflowWrap: 'anywhere' }}
                        >
                          {investigation.title}
                        </Typography>
                      }
                      secondary={
                        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mt: 0.5 }}>
                          <Typography component="span" variant="caption" sx={{ fontSize: '0.7rem' }}>
                            {safeFormatRelative(investigation.updated_at)}
                          </Typography>
                          <Chip
                            label={investigation.state}
                            size="small"
                            color="secondary"
                            variant="outlined"
                            icon={<ViewIcon sx={{ fontSize: '0.75rem' }} />}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewDetails(investigation.id);
                            }}
                            sx={{
                              fontSize: '0.75rem',
                              height: 20,
                              textTransform: 'capitalize',
                              cursor: 'pointer',
                              '&:hover': {
                                backgroundColor: 'secondary.main',
                                color: 'secondary.contrastText',
                              },
                            }}
                          />
                        </Box>
                      }
                      primaryTypographyProps={{
                        fontSize: '0.875rem',
                        color: investigation.id === currentInvestigationId ? 'inherit' : 'text.primary',
                      }}
                      secondaryTypographyProps={{
                        component: 'div',
                        color: investigation.id === currentInvestigationId ? 'inherit' : 'text.secondary',
                      }}
                    />

                    <IconButton
                      size="small"
                      onClick={(e) => handleMenuOpen(e, investigation.id)}
                      sx={{
                        opacity: 0.6,
                        '&:hover': { opacity: 1 },
                      }}
                    >
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </ListItemButton>
                </ListItem>
              ))}

              {hiddenCount > 0 && (
                <ListItemButton
                  onClick={() => setExpandedGroups(prev => ({ ...prev, [status]: true }))}
                  sx={{ borderRadius: 2, mx: 1, mb: 1, justifyContent: 'center' }}
                >
                  <ListItemText
                    primary={`Show ${hiddenCount} more`}
                    primaryTypographyProps={{ variant: 'caption', color: 'text.secondary', textAlign: 'center' }}
                  />
                </ListItemButton>
              )}

              {expanded && investigationsInGroup.length > COLLAPSED_GROUP_SIZE && (
                <ListItemButton
                  onClick={() => setExpandedGroups(prev => ({ ...prev, [status]: false }))}
                  sx={{ borderRadius: 2, mx: 1, mb: 1, justifyContent: 'center' }}
                >
                  <ListItemText
                    primary="Show less"
                    primaryTypographyProps={{ variant: 'caption', color: 'text.secondary', textAlign: 'center' }}
                  />
                </ListItemButton>
              )}
            </Box>
            );
          })
        )}
      </List>

      <InvestigationContextMenu
        anchorEl={menuAnchor}
        investigation={selectedInvestigationObj}
        onClose={handleMenuClose}
        onManageScope={handleManageScope}
        onDelete={handleDeleteClick}
        onUpdateStatus={handleUpdateStatus}
      />

      {selectedInvestigationObj && (
        <InvestigationScopeModal
          isOpen={showScopeModal}
          customerId={selectedInvestigationObj.customer_id || ''}
          title="Manage Investigation Scope"
          initialScope={selectedInvestigationObj.scope || []}
          onSave={handleScopeModalSave}
          onCancel={handleScopeModalCancel}
        />
      )}

      <InvestigationDetailsDialog
        open={showDetailsModal}
        investigation={selectedInvestigationObj || null}
        onClose={handleDetailsModalClose}
        onSave={handleDetailsModalSave}
      />

      <DeleteConfirmDialog
        open={showDeleteConfirm}
        title="Delete Investigation?"
        onCancel={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        maxWidth="xs"
        destructive={false}
      >
        <Typography>Are you sure you want to delete this investigation?</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          This action cannot be undone. All data associated with this investigation will be permanently deleted.
        </Typography>
      </DeleteConfirmDialog>
    </Box>
  );
};

export default InvestigationSidebar;
